import {PayoutCreditorKind, TransferType} from '@sollapay/enums';
import {
  Badge,
  Icon,
  InfoTooltip,
  MoreMenu,
  Table,
  TableActionsCell,
  Typography,
  nodeColumn
} from '@sollapay/ui/components';
import {parseMoney} from '@sollapay/utils';
import {
  PayoutPayeeCell,
  ReferenceCodeBadge,
  resolvePayoutPayeeDisplayName
} from '@sollapay/web-core';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {AmountCell, DateCell} from '@/components';
import {MasavTransferBadge, ZahavTransferBadge} from '@/features/buyers/components';
import {
  resolveAuthorityDisplayName,
  resolvePayoutDescription,
  resolvePayoutRoleLabel
} from '@/features/payouts';

import {BankCheckTransferBadge} from './BankCheckTransferBadge';
import {canCancelPayout, getPayoutStatusDisplay} from './payout-status.utils';
import {VoucherTransferBadge} from './VoucherTransferBadge';

import type {PayoutTransferType} from '@sollapay/enums';
import type {PayoutInstructionDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface PayoutsTableProps {
  payouts: PayoutInstructionDTO[];
  /** Omitted when the viewer lacks payout write access — the row menu then renders nothing. */
  onCancel?: (payout: PayoutInstructionDTO) => void;
}

export const PayoutsTable: FC<PayoutsTableProps> = ({payouts, onCancel}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const prefix = 'view.payouts.list';

  const columns = useMemo(
    () => [
      nodeColumn<PayoutInstructionDTO>({
        id: 'beneficiary',
        header: t(`${prefix}.columns.beneficiary`),
        accessorFn: row => resolvePayoutPayeeDisplayName(row.creditor, resolveAuthorityDisplayName),
        cell: row => (
          <PayoutPayeeCell
            payee={row.creditor}
            resolveAuthorityName={resolveAuthorityDisplayName}
          />
        )
      }),
      nodeColumn<PayoutInstructionDTO>({
        id: 'role',
        header: t(`${prefix}.columns.role`),
        accessorFn: row =>
          row.creditor.kind === PayoutCreditorKind.BENEFICIARY
            ? row.creditor.role
            : PayoutCreditorKind.AUTHORITY,
        cell: row => (
          <Badge variant="neutral" size="sm">
            {resolvePayoutRoleLabel(row.creditor, t)}
          </Badge>
        )
      }),
      nodeColumn<PayoutInstructionDTO>({
        id: 'purpose',
        header: t(`${prefix}.columns.purpose`),
        accessorFn: row => resolvePayoutDescription(row.creditor, t),
        cell: row => (
          <Typography size="sm" color="secondary">
            {resolvePayoutDescription(row.creditor, t)}
          </Typography>
        )
      }),
      nodeColumn<PayoutInstructionDTO>({
        id: 'amount',
        header: t(`${prefix}.columns.amount`),
        accessorFn: row => parseMoney(row.amount).toNumber(),
        cell: row => <AmountCell amount={row.amount} />
      }),
      nodeColumn<PayoutInstructionDTO>({
        id: 'reference',
        header: t(`${prefix}.columns.reference`),
        accessorFn: row => row.referenceCode,
        cell: row => (
          <ReferenceCodeBadge
            code={row.referenceCode}
            fieldName={t('common:fields.referenceCode.label')}
          />
        )
      }),
      nodeColumn<PayoutInstructionDTO>({
        id: 'transferType',
        header: t(`${prefix}.columns.transferType`),
        accessorFn: row => row.transferType ?? '',
        cell: row =>
          row.transferType ? <PayoutTransferTypeBadge transferType={row.transferType} /> : null
      }),
      nodeColumn<PayoutInstructionDTO>({
        id: 'status',
        header: t(`${prefix}.columns.status`),
        accessorFn: row => row.status,
        cell: row => {
          const {variant, icon} = getPayoutStatusDisplay(row.status);
          return (
            <div className="flex items-center gap-1.5">
              <Badge variant={variant} size="sm">
                <Icon name={icon} className="size-3" />
                {t(`${prefix}.status.${row.status}`)}
              </Badge>
              <InfoTooltip content={t(`${prefix}.status.${row.status}_tooltip`)} />
            </div>
          );
        }
      }),
      nodeColumn<PayoutInstructionDTO>({
        id: 'createdAt',
        header: t(`${prefix}.columns.createdAt`),
        accessorFn: row => row.createdAt,
        cell: row => <DateCell date={row.createdAt} />
      }),
      nodeColumn<PayoutInstructionDTO>({
        id: 'actions',
        header: '',
        width: 'min',
        // 52px matches the skeleton's actions track.
        maxWidth: 52,
        enableSorting: false,
        cell: row => {
          if (!onCancel || !canCancelPayout(row.status)) return null;
          return (
            <TableActionsCell>
              <MoreMenu
                ariaLabel={t(`${prefix}.tableActions.openMenu`)}
                items={[
                  {
                    icon: 'credit-card-cancel',
                    label: t(`${prefix}.tableActions.cancel`),
                    onClick: () => onCancel(row),
                    destructive: true
                  }
                ]}
              />
            </TableActionsCell>
          );
        }
      })
    ],
    [t, prefix, onCancel]
  );

  return <Table columns={columns} data={payouts} />;
};

interface PayoutTransferTypeBadgeProps {
  transferType: PayoutTransferType;
}

const PayoutTransferTypeBadge: FC<PayoutTransferTypeBadgeProps> = ({transferType}) => {
  if (transferType === TransferType.ZAHAV) return <ZahavTransferBadge />;
  if (transferType === TransferType.MASAV) return <MasavTransferBadge />;
  if (transferType === TransferType.VOUCHER) return <VoucherTransferBadge />;

  return <BankCheckTransferBadge />;
};
